import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useRole } from "@/contexts/RoleContext";
import { quartiersRepository } from "@/repositories/quartiersRepository";
import type { QuartierRow, QuartierUpdate } from "@/schemas/quartier";

export const quartiersKeys = {
  all: ["quartiers"] as const,
  list: (role: string) => ["quartiers", "list", role] as const,
  detail: (role: string, id: string) => ["quartiers", "detail", role, id] as const,
};

export function useQuartiers() {
  const { role } = useRole();
  return useQuery<QuartierRow[]>({
    queryKey: quartiersKeys.list(role),
    queryFn: () => quartiersRepository.list(role),
  });
}

export function useQuartier(id: string | undefined) {
  const { role } = useRole();
  return useQuery<QuartierRow | null>({
    queryKey: id ? quartiersKeys.detail(role, id) : ["quartiers", "detail", role, "__none__"],
    queryFn: () => (id ? quartiersRepository.get(role, id) : Promise.resolve(null)),
    enabled: !!id,
  });
}

export function useUpdateQuartier(id: string) {
  const { role } = useRole();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: QuartierUpdate) => quartiersRepository.update(role, id, patch),
    onSuccess: (updated) => {
      qc.setQueryData(quartiersKeys.detail(role, id), updated);
      qc.invalidateQueries({ queryKey: quartiersKeys.all });
    },
  });
}
